import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import SchemaOrg from "@/components/base/SchemaOrg";
import Breadcrumb from "@/components/base/Breadcrumb";
import { aboutData } from "@/mocks/aboutData";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    setFormError("");

    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="contact_alt"]');
    if (honeypot?.value.trim()) {
      setFormStatus("success");
      form.reset();
      return;
    }

    const formData = new FormData(form);
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "contact_alt") {
        payload.append(key, value.toString());
      }
    });

    try {
      const response = await fetch("https://readdy.ai/api/form/d969v5npg5pqhofcumg0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      const serverMsg =
        (parsed as Record<string, unknown>)?.meta?.message ||
        (parsed as Record<string, unknown>)?.message ||
        (parsed as Record<string, unknown>)?.meta?.detail ||
        responseText;

      const isSpam =
        typeof serverMsg === "string" &&
        (serverMsg.toLowerCase().includes("spam") || serverMsg.toLowerCase().includes("form data is spam"));

      const code = (parsed as Record<string, unknown>)?.code;

      if (response.ok && code === "OK" && !isSpam) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
        setFormError(typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后重试");
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请检查网络后重试");
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 | 51漫画 - 品牌介绍、平台数据与联系方式"
        description="51漫画是国内领先的免费漫画阅读平台，创立于2018年。了解我们的品牌故事、平台数据、用户口碑以及商务合作联系方式。"
        keywords="51漫画, 关于我们, 51漫画平台, 漫画网站, 商务合作, 联系方式"
        canonicalUrl="https://www.51manhua.com/about"
      />

      <SchemaOrg
        type="organization"
        data={{
          "@type": "Organization",
          name: "51漫画",
          url: "https://www.51manhua.com/",
          description: aboutData.brandStory.title,
          foundingDate: aboutData.stats.find((s) => s.label === "成立年份")?.value || "2018",
          contactPoint: {
            "@type": "ContactPoint",
            email: aboutData.contact.email,
            telephone: aboutData.contact.phone,
            contactType: "customer service",
          },
          address: {
            "@type": "PostalAddress",
            addressLocality: "北京",
            addressRegion: "海淀区",
            addressCountry: "CN",
            streetAddress: aboutData.contact.address,
          },
        }}
      />

      <SchemaOrg
        type="breadcrumb"
        data={{
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "https://www.51manhua.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "关于我们",
              item: "https://www.51manhua.com/about",
            },
          ],
        }}
      />

      <Navbar />

      <main className="pt-16 md:pt-18">
        <section className="py-14 md:py-20 bg-foreground-950">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-1 text-center lg:text-left">
                <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium bg-accent-500/20 text-accent-300 border border-accent-500/30 mb-6">
                  关于51漫画
                </span>
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-background-50 leading-tight text-balance">
                  关于我们
                </h1>
                <p className="mt-4 text-base md:text-lg text-foreground-300 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                  {aboutData.brandStory.title}
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-3 mt-8 justify-center lg:justify-start">
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-accent-500 text-background-50 font-semibold text-sm hover:bg-accent-600 transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-mail-line w-4 h-4 flex items-center justify-center" />
                    联系我们
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-background-50/30 text-background-50 font-semibold text-sm hover:bg-background-50/10 transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-home-line w-4 h-4 flex items-center justify-center" />
                    返回首页
                  </Link>
                </div>
              </div>
              <div className="flex-1 w-full max-w-lg lg:max-w-none">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={aboutData.brandStory.image}
                    alt="51漫画创作团队"
                    className="w-full aspect-[16/9] object-cover object-top"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: "关于我们" },
          ]}
        />

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="flex-1">
                <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 mb-4">
                  品牌故事
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-tight text-balance">
                  {aboutData.brandStory.title}
                </h2>
                <div className="mt-6 space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  {aboutData.brandStory.content.map((paragraph, i) => (
                    <p key={i}>{paragraph}</p>
                  ))}
                </div>
              </div>
              <div className="flex-1">
                <div className="bg-accent-500 rounded-3xl p-6 md:p-8 text-background-50">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-medium bg-background-50/20 px-3 py-1 rounded-full">
                      创始人寄语
                    </span>
                    <div className="flex gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-background-50/60" />
                      <span className="w-1.5 h-1.5 rounded-full bg-background-50/60" />
                      <span className="w-1.5 h-1.5 rounded-full bg-background-50/60" />
                    </div>
                  </div>
                  <blockquote className="text-base md:text-lg font-medium leading-relaxed">
                    "我们的目标很简单：让每一位漫画爱好者都能免费、便捷地读到最好的漫画。"
                  </blockquote>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-50/20">
                      <img
                        src={aboutData.team[0].image}
                        alt={aboutData.team[0].name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{aboutData.team[0].name}</p>
                      <p className="text-xs text-background-50/70">{aboutData.team[0].role}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 mb-4">
                核心数据
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 text-balance">
                平台<span className="text-primary-500">实力</span>数据
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {aboutData.stats.map((stat) => (
                <div key={stat.label} className="bg-background-50 rounded-2xl p-6 md:p-8 text-center">
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500 mx-auto mb-4">
                    <i className={`${stat.icon} text-xl md:text-2xl w-6 h-6 flex items-center justify-center`} />
                  </div>
                  <p className="font-heading text-2xl md:text-3xl font-black text-foreground-950">{stat.value}</p>
                  <p className="text-sm text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="testimonials" className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700 mb-4">
                用户口碑
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 text-balance">
                来自读者的<span className="text-accent-500">真实评价</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {aboutData.testimonials.map((t) => (
                <div key={t.name} className="bg-background-100 rounded-2xl p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full overflow-hidden bg-background-200">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover object-top" loading="lazy" />
                    </div>
                    <div>
                      <p className="font-heading text-sm font-semibold text-foreground-950">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700 mb-4">
                商务合作
              </span>
              <h2 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 text-balance">
                与我们<span className="text-primary-500">合作</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {aboutData.cooperation.items.map((item) => (
                <div key={item.title} className="bg-background-50 rounded-2xl p-6 md:p-8">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-500 mb-4">
                    <i className="ri-customer-service-2-line text-xl w-6 h-6 flex items-center justify-center" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-4">{item.description}</p>
                  <a href={`mailto:${item.email}`} className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 transition-colors duration-200">
                    <i className="ri-mail-line w-4 h-4 flex items-center justify-center" />
                    {item.email}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 bg-foreground-950">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="flex-1">
                <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium border border-background-50/20 text-background-50/70 mb-4">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-black text-background-50 leading-tight text-balance">
                  商务合作与<span className="text-accent-500">意见反馈</span>
                </h2>
                <p className="mt-4 text-sm md:text-base text-foreground-400 leading-relaxed">
                  无论是商务合作、广告投放、作者入驻，还是对平台的意见反馈，我们都期待与您的沟通。
                </p>

                <div className="mt-8 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50/10 text-accent-400">
                      <i className="ri-mail-line w-5 h-5 flex items-center justify-center" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">联系邮箱</p>
                      <p className="text-sm text-background-50 font-medium">{aboutData.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50/10 text-accent-400">
                      <i className="ri-phone-line w-5 h-5 flex items-center justify-center" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">客服电话</p>
                      <p className="text-sm text-background-50 font-medium">{aboutData.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50/10 text-accent-400">
                      <i className="ri-map-pin-line w-5 h-5 flex items-center justify-center" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">公司地址</p>
                      <p className="text-sm text-background-50 font-medium">{aboutData.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-50/10 text-accent-400">
                      <i className="ri-time-line w-5 h-5 flex items-center justify-center" />
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">工作时间</p>
                      <p className="text-sm text-background-50 font-medium">{aboutData.contact.workHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form="d969v5npg5pqhofcumg0"
                  className="bg-background-50 rounded-2xl p-6 md:p-8"
                >
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-6">发送消息</h3>

                  <div className="form-anti-spam-wrapper">
                    <input
                      type="text"
                      name="contact_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-950 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-950 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-950 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      >
                        <option value="">请选择主题</option>
                        <option value="商务合作">商务合作</option>
                        <option value="广告投放">广告投放</option>
                        <option value="作者入驻">作者入驻</option>
                        <option value="意见反馈">意见反馈</option>
                        <option value="其他">其他</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-950 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      />
                      <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                    </div>
                  </div>

                  {formStatus === "error" && (
                    <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm">
                      {formError}
                    </div>
                  )}

                  {formStatus === "success" && (
                    <div className="mt-4 p-3 rounded-lg bg-green-50 text-green-600 text-sm">
                      消息已发送成功！我们会尽快与您联系。
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === "submitting"}
                    className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formStatus === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin" />
                        发送中...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center" />
                        发送消息
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}